import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";

const QrPhoneInputPage = () => {
  const [searchParams] = useSearchParams();
  const session = searchParams.get("session") || "";
  const apiAddr = searchParams.get("api") || "";

  const [name, setName] = useState("");
  const [pnum, setPnum] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const [errMsg, setErrMsg] = useState("");
  const [debugInfo, setDebugInfo] = useState("");

  // 전화번호 자동 하이픈 포맷
  const formatPhone = (val) => {
    const digits = val.replace(/\D/g, "").slice(0, 11);
    if (digits.length <= 3) return digits;
    if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setErrMsg("이름을 입력해주세요."); return; }
    const phone = pnum.replace(/-/g, "");
    if (phone.length < 10 || phone.length > 11) { setErrMsg("전화번호를 정확히 입력해주세요."); return; }
    if (!session || !apiAddr) { setErrMsg("세션 정보가 없습니다. QR 코드를 다시 스캔하세요."); return; }

    setStatus("sending");
    setErrMsg("");
    const postUrl = `${apiAddr}/post/qrpnum`;
    const payload = { session, name: name.trim(), pnum: pnum.trim() };
    console.log("[QR-INPUT] POST →", postUrl, payload);
    setDebugInfo(`POST: ${postUrl}\n${JSON.stringify(payload, null, 2)}`);
    try {
      const res = await axios.post(postUrl, payload);
      console.log("[QR-INPUT] 응답:", res.data);
      setDebugInfo(prev => prev + `\n응답: ${JSON.stringify(res.data)}`);
      if (res.data?.result === "success" || res.status === 200) {
        setStatus("done");
        setName("");
        setPnum("");
      } else {
        setStatus("error");
        setErrMsg(`서버 응답: ${JSON.stringify(res.data)}`);
      }
    } catch (err) {
      const detail = err.response
        ? `HTTP ${err.response.status}: ${JSON.stringify(err.response.data)}`
        : err.message;
      console.error("[QR-INPUT] 오류:", detail);
      setDebugInfo(prev => prev + `\n오류: ${detail}`);
      setStatus("error");
      setErrMsg(`전송 실패 — ${detail}`);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {/* 헤더 */}
        <div style={styles.header}>
          <div style={styles.headerIcon}>📱</div>
          <h2 style={styles.title}>선탑자 정보 입력</h2>
          <p style={styles.subtitle}>이름과 전화번호를 입력하고 전송하세요</p>
        </div>

        {/* 완료 상태 */}
        {status === "done" ? (
          <div style={styles.doneBox}>
            <div style={{ fontSize: 48 }}>✅</div>
            <p style={{ fontSize: "1.1rem", fontWeight: "bold", color: "#2e7d32", margin: "8px 0" }}>전송 완료!</p>
            <p style={{ color: "#555", fontSize: "0.9rem" }}>정보가 등록되었습니다.</p>
            <button style={styles.btnOutline} onClick={() => setStatus("idle")}>
              추가 입력
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={styles.form}>
            {/* 이름 */}
            <label style={styles.label}>이름 *</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="홍길동"
              style={styles.input}
              maxLength={20}
              autoComplete="name"
            />

            {/* 전화번호 */}
            <label style={styles.label}>전화번호 *</label>
            <input
              type="tel"
              value={pnum}
              onChange={e => setPnum(formatPhone(e.target.value))}
              placeholder="010-0000-0000"
              style={styles.input}
              maxLength={13}
              autoComplete="tel"
              inputMode="tel"
            />

            {/* 에러 메시지 */}
            {errMsg && <p style={styles.errMsg}>{errMsg}</p>}

            {/* 전송 버튼 */}
            <button
              type="submit"
              style={{ ...styles.btn, opacity: status === "sending" ? 0.7 : 1 }}
              disabled={status === "sending"}
            >
              {status === "sending" ? "전송 중..." : "전송"}
            </button>

            {/* 세션 정보 (작게) */}
            <p style={styles.sessionInfo}>
              세션: {session || "없음"}<br />
              API: {apiAddr || "없음"}
            </p>
            {/* 디버그 패널 */}
            {debugInfo && (
              <pre style={styles.debugBox}>{debugInfo}</pre>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: "100vh",
    backgroundColor: "#e8f5e9",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "'Noto Sans KR', 'Malgun Gothic', sans-serif",
    padding: 16,
  },
  card: {
    backgroundColor: "white",
    borderRadius: 16,
    boxShadow: "0 4px 24px rgba(0,0,0,0.13)",
    width: "100%",
    maxWidth: 380,
    overflow: "hidden",
  },
  header: {
    backgroundColor: "#2e7d32",
    color: "white",
    padding: "24px 20px 20px",
    textAlign: "center",
  },
  headerIcon: { fontSize: 36, marginBottom: 6 },
  title: { margin: 0, fontSize: "1.3rem", fontWeight: "bold" },
  subtitle: { margin: "6px 0 0", fontSize: "0.85rem", opacity: 0.85 },
  form: {
    padding: "24px 20px",
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  label: {
    fontSize: "0.85rem",
    fontWeight: "bold",
    color: "#333",
    marginBottom: 2,
    marginTop: 12,
  },
  input: {
    width: "100%",
    padding: "12px 14px",
    fontSize: "1rem",
    border: "1.5px solid #ccc",
    borderRadius: 8,
    boxSizing: "border-box",
    outline: "none",
    transition: "border-color 0.2s",
  },
  errMsg: {
    color: "#d32f2f",
    fontSize: "0.82rem",
    margin: "4px 0 0",
  },
  btn: {
    marginTop: 20,
    width: "100%",
    padding: "13px",
    backgroundColor: "#2e7d32",
    color: "white",
    border: "none",
    borderRadius: 8,
    fontSize: "1rem",
    fontWeight: "bold",
    cursor: "pointer",
    transition: "background 0.2s",
  },
  btnOutline: {
    marginTop: 14,
    padding: "10px 24px",
    backgroundColor: "white",
    color: "#2e7d32",
    border: "2px solid #2e7d32",
    borderRadius: 8,
    fontSize: "0.95rem",
    fontWeight: "bold",
    cursor: "pointer",
  },
  sessionInfo: {
    marginTop: 16,
    fontSize: "0.68rem",
    color: "#bbb",
    textAlign: "center",
    wordBreak: "break-all",
  },
  debugBox: {
    marginTop: 8,
    width: "100%",
    padding: "8px",
    backgroundColor: "#1a1a2e",
    color: "#a5d6a7",
    fontSize: "0.65rem",
    borderRadius: 6,
    overflowX: "auto",
    whiteSpace: "pre-wrap",
    wordBreak: "break-all",
    boxSizing: "border-box",
  },
  doneBox: {
    padding: "32px 20px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
    textAlign: "center",
  },
};

export default QrPhoneInputPage;
